import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "./components/ui/sonner";
import { AppProvider, useApp } from "./context/AppContext";
import { AppShell } from "./components/AppShell";
import { registerSW } from "./lib/push";
import Login from "./pages/Login";
import { Forgot, Reset } from "./pages/PasswordPages";
import InvitePage from "./pages/InvitePage";
import Home from "./pages/Home";
import Planning from "./pages/Planning";
import Availability from "./pages/Availability";
import Missions from "./pages/Missions";
import MissionForm from "./pages/MissionForm";
import MissionDetail from "./pages/MissionDetail";
import Team from "./pages/Team";
import StructureForm from "./pages/StructureForm";
import SettingsPage from "./pages/Settings";
import Account from "./pages/Account";
import Register from "./pages/Register";
import Avelis, { Legal } from "./pages/Avelis";
import Notifications from "./pages/Notifications";
import Recap from "./pages/Recap";
import Help, { HelpModule } from "./pages/Help";
import Assistance, { AssistanceDetail } from "./pages/Assistance";
import { AdminShell } from "./admin/AdminShell";
import AdminDashboard from "./admin/Dashboard";
import AdminAccounts from "./admin/Accounts";
import AdminAccountDetail from "./admin/AccountDetail";
import AdminUsers from "./admin/Users";
import AdminSupport, { TicketDetail } from "./admin/Support";
import { Crm, Audit, AdminSettings } from "./admin/Misc";

function Protected({ children }) {
  const { me } = useApp();
  const loc = useLocation();
  if (me === null) return <div className="flex min-h-screen items-center justify-center text-teal-700">Chargement…</div>;
  if (me === false) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
}

function AdminRedirect() {
  const loc = useLocation();
  const u = new URLSearchParams(loc.search).get("user");
  return u ? <Navigate to={`/admin/utilisateurs?user=${u}`} replace /> : <AdminDashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/inscription" element={<Register />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/mot-de-passe-oublie" element={<Forgot />} />
      <Route path="/reset/:token" element={<Reset />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route element={<Protected><AdminShell /></Protected>}>
        <Route path="/admin" element={<AdminRedirect />} />
        <Route path="/admin/comptes" element={<AdminAccounts />} />
        <Route path="/admin/comptes/:sid" element={<AdminAccountDetail />} />
        <Route path="/admin/utilisateurs" element={<AdminUsers />} />
        <Route path="/admin/assistance" element={<AdminSupport />} />
        <Route path="/admin/assistance/:id" element={<TicketDetail />} />
        <Route path="/admin/suivi" element={<Crm />} />
        <Route path="/admin/parametres" element={<AdminSettings />} />
        <Route path="/admin/journal" element={<Audit />} />
      </Route>
      <Route element={<Protected><AppShell bare /></Protected>}>
        <Route path="/structure/nouvelle" element={<StructureForm />} />
        <Route path="/assistance" element={<Assistance />} />
        <Route path="/assistance/:id" element={<AssistanceDetail />} />
        <Route path="/compte" element={<Account />} />
        <Route path="/avelis" element={<Avelis />} />
        <Route path="/recap" element={<Recap />} />
        <Route path="/aide" element={<Help />} />
        <Route path="/aide/:id" element={<HelpModule />} />
      </Route>
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/" element={<Home />} />
        <Route path="/planning" element={<Planning />} />
        <Route path="/disponibilites" element={<Availability />} />
        <Route path="/missions" element={<Missions />} />
        <Route path="/missions/nouvelle" element={<MissionForm />} />
        <Route path="/missions/:id" element={<MissionDetail />} />
        <Route path="/equipe" element={<Team />} />
        <Route path="/parametres" element={<SettingsPage />} />
        <Route path="/notifications" element={<Notifications />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    registerSW();
  }, []);
  return (
    <AppProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AppProvider>
  );
}
